import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ShoppingBag, ThermometerSun, Shirt, Heart, Shield, X } from 'lucide-react';
import ShareResult from '../ShareResult';

const QUIZ_QUESTIONS = [
  { id: 'goal', q: 'What\'s your main reason for considering a pump cover?', options: [
    { label: 'Maximize the "pump" effect and sweating', score: 3 },
    { label: 'Comfort and style at the gym', score: 2 },
    { label: 'Modesty / confidence boost', score: 2 },
    { label: 'Keeping muscles warm during warm-up', score: 1 },
  ]},
  { id: 'heat', q: 'How well do you handle heat during exercise?', options: [
    { label: 'Love it — the more I sweat, the better', score: 3 },
    { label: 'Fine as long as I stay hydrated', score: 2 },
    { label: 'I tend to overheat easily', score: 0 },
  ]},
  { id: 'workout', q: 'What type of workout do you typically do?', options: [
    { label: 'Heavy lifting / bodybuilding', score: 3 },
    { label: 'Mixed strength & cardio', score: 2 },
    { label: 'Mostly cardio / HIIT', score: 1 },
    { label: 'Yoga / flexibility', score: 0 },
  ]},
  { id: 'hydration', q: 'How disciplined are you with hydration?', options: [
    { label: 'Always have water with me, drink consistently', score: 3 },
    { label: 'Usually good, sometimes forget', score: 2 },
    { label: 'Often forget to drink enough', score: 0 },
  ]},
];

const VERDICTS = [
  { min: 0, max: 4, label: 'Skip It', color: '#ef4444', desc: 'A pump cover might not be the best fit for your workout style. Risk of overheating outweighs the benefits.', icon: X },
  { min: 5, max: 7, label: 'Try It Sometimes', color: '#f59e0b', desc: 'A pump cover could work for your warm-up sets or heavy lifting days. Remove it during intense cardio.', icon: Shield },
  { min: 8, max: 12, label: 'Go For It', color: '#22c55e', desc: 'You\'re a great candidate for a pump cover! Your workout style and hydration habits support it.', icon: Check },
];

export default function PumpCoversArticle() {
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [showResult, setShowResult] = useState(false);

  const handleAnswer = (qId, score) => {
    const updated = { ...answers, [qId]: score };
    setAnswers(updated);
    if (step < QUIZ_QUESTIONS.length - 1) {
      setTimeout(() => setStep(s => s + 1), 200);
    } else {
      setTimeout(() => setShowResult(true), 200);
    }
  };

  const total = Object.values(answers).reduce((s, v) => s + v, 0);
  const verdict = VERDICTS.find(v => total >= v.min && total <= v.max) || VERDICTS[0];

  return (
    <main className="pt-20 min-h-screen bg-[#0a0a0a]" data-testid="pump-covers-article">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/blog" className="text-zinc-500 text-xs uppercase tracking-wider hover:text-white transition-colors mb-4 inline-block" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Back to Blog</Link>

        <h1 className="text-4xl sm:text-5xl text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="pump-covers-title">Why Do People Wear Pump Covers?</h1>
        <p className="text-zinc-400 text-lg mb-8 leading-relaxed">The gym trend explained — from maximizing the "pump" effect to oversized comfort and style.</p>

        {/* Article Content */}
        <div className="prose-ninja mb-10 space-y-6 text-zinc-300 leading-relaxed">
          <h2 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>What is a Pump Cover?</h2>
          <p>A pump cover is an oversized piece of workout clothing — usually a hoodie, sweatshirt, or baggy t-shirt — designed to be worn on top of your gym wear. They're typically a few sizes larger than normal.</p>

          <h2 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Why Do People Wear Them?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { title: 'Maximize the Pump', desc: 'Traps heat, exaggerating the muscle-swelling "pump" effect from blood flow.', icon: ThermometerSun, color: '#E63946' },
              { title: 'Comfort & Style', desc: 'Oversized fit provides full range of motion and gym-fashion cred.', icon: Shirt, color: '#00F0FF' },
              { title: 'Warm-Up Aid', desc: 'Keeps muscles loose and warm during initial sets, reducing injury risk.', icon: Shield, color: '#f59e0b' },
              { title: 'Confidence Boost', desc: 'Some feel less self-conscious in oversized clothing, especially beginners.', icon: Heart, color: '#22c55e' },
            ].map(r => (
              <div key={r.title} className="border border-zinc-800 p-4 bg-zinc-900/40">
                <r.icon className="w-5 h-5 mb-2" style={{ color: r.color }} />
                <h4 className="text-white text-sm mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{r.title}</h4>
                <p className="text-zinc-400 text-xs leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Interactive: Should You Wear a Pump Cover? */}
        <div className="border border-[#E63946]/30 bg-[#E63946]/5 p-6 mb-10" data-testid="pump-cover-quiz">
          <div className="flex items-center gap-2 mb-1">
            <Shirt className="w-5 h-5 text-[#E63946]" />
            <h3 className="text-white text-xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Should You Wear a Pump Cover?</h3>
          </div>
          <p className="text-zinc-500 text-xs mb-5">Take this quick quiz to find out.</p>

          {!showResult ? (
            <>
              <div className="flex gap-1 mb-4">
                {QUIZ_QUESTIONS.map((q, i) => (
                  <div key={q.id} className={`flex-1 h-1 ${i <= step ? 'bg-[#E63946]' : 'bg-zinc-800'} transition-all`} />
                ))}
              </div>
              <p className="text-white text-sm mb-4">{QUIZ_QUESTIONS[step].q}</p>
              <div className="space-y-2">
                {QUIZ_QUESTIONS[step].options.map(opt => (
                  <button key={opt.label}
                    onClick={() => handleAnswer(QUIZ_QUESTIONS[step].id, opt.score)}
                    className="w-full text-left px-4 py-3 border border-zinc-800 text-zinc-400 text-sm hover:border-[#E63946]/40 hover:text-white transition-all"
                    data-testid={`pump-opt-${opt.label.slice(0, 15).replace(/\s/g, '-').toLowerCase()}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
              {step > 0 && <button onClick={() => setStep(s => s - 1)} className="mt-3 text-zinc-500 text-xs hover:text-white transition-colors">Back</button>}
            </>
          ) : (
            <div className="border border-zinc-800 bg-zinc-900/60 p-6 text-center" data-testid="pump-quiz-result">
              <verdict.icon className="w-8 h-8 mx-auto mb-2" style={{ color: verdict.color }} />
              <p className="text-xl mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif', color: verdict.color }}>{verdict.label}</p>
              <p className="text-zinc-400 text-sm max-w-md mx-auto mb-4">{verdict.desc}</p>
              <div className="flex items-center justify-center gap-3">
                <ShareResult
                  text={`Should I wear a pump cover? My result: ${verdict.label}! Take the quiz:`}
                  url="https://ninjathlete.com/blogs/article/why-do-people-wear-pump-covers"
                  label="Share Result"
                />
                <button onClick={() => { setAnswers({}); setStep(0); setShowResult(false); }}
                  className="px-4 py-2 border border-zinc-700 text-zinc-400 text-xs uppercase tracking-wider hover:text-white transition-colors"
                  data-testid="pump-retake">Retake</button>
              </div>
            </div>
          )}
        </div>

        {/* Drawbacks */}
        <div className="prose-ninja space-y-6 text-zinc-300 leading-relaxed mb-10">
          <h2 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Potential Drawbacks</h2>
          <ul className="space-y-2">
            <li><strong className="text-[#E63946]">Overheating</strong> — Monitor yourself closely. Take breaks and remove the cover when needed.</li>
            <li><strong className="text-[#E63946]">Dehydration</strong> — Increased sweat loss requires extra hydration before, during, and after.</li>
            <li><strong className="text-[#E63946]">Body Image</strong> — "Pump cover reveals" on social media can perpetuate unrealistic body standards.</li>
          </ul>

          <h2 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>The Bottom Line</h2>
          <p>A pump cover is a tool, not a shortcut. If you enjoy the enhanced sweat, warmth, and pumped look — or simply like the style — go for it. Just stay hydrated, listen to your body, and keep the focus on your fitness goals.</p>
        </div>

        {/* Shop CTA */}
        <div className="border border-[#E63946]/30 bg-[#E63946]/5 p-6 text-center" data-testid="shop-pump-covers-cta">
          <ShoppingBag className="w-8 h-8 text-[#E63946] mx-auto mb-3" />
          <h3 className="text-white text-xl mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Shop NinjAthlete Pump Covers</h3>
          <p className="text-zinc-400 text-sm mb-4">Premium oversized gym wear built for the pump.</p>
          <Link to="/shop" className="inline-flex items-center gap-2 px-6 py-3 bg-[#E63946] text-white text-xs uppercase tracking-wider hover:opacity-90 transition-opacity" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="shop-pump-btn">
            Shop Now <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </main>
  );
}
